import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import dryftService from '../services/dryftService';
import './AddPaymentPorterWizard.css';
import './OperaCloudCompare.css';

// Create/edit a Dryft Configuration Set — a named bundle of OHIP config CHECKS. The catalog (GET
// /dryft/catalog) gives groups → sub-areas → checks. A sub-area can be watched WHOLE (all its checks,
// stored as check_key = null) or FINE-GRAINED (specific checks). Selection model: a Map keyed by
// subarea_key whose value is either 'ALL' or a Set of check keys.

const BACK = '/apps/dryft?tab=config-sets';

const DryftConfigSetForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const { selectedAccount, selectedPartner } = useAccount();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [active, setActive] = useState(true);
    const [sel, setSel] = useState(() => new Map());        // subarea_key -> 'ALL' | Set(check_key)
    const [expanded, setExpanded] = useState(() => new Set()); // expanded sub-areas
    const [groups, setGroups] = useState([]);               // [{ group_key, group_label, subAreas: [...] }]
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [nameErr, setNameErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [catRes, setRes] = await Promise.all([
                dryftService.getCatalog(),
                isEdit ? dryftService.getConfigSet(id) : Promise.resolve(null),
            ]);

            const subAreas = catRes.data?.subAreas || [];
            const byGroup = [];
            const idx = {};
            for (const sa of subAreas) {
                if (idx[sa.group_key] === undefined) {
                    idx[sa.group_key] = byGroup.length;
                    byGroup.push({ group_key: sa.group_key, group_label: sa.group_label || sa.group_key, subAreas: [] });
                }
                byGroup[idx[sa.group_key]].subAreas.push(sa);
            }
            setGroups(byGroup);

            if (setRes) {
                const cs = setRes.data?.configSet;
                if (cs) {
                    setName(cs.name || '');
                    setDescription(cs.description || '');
                    setActive(cs.active !== 0 && cs.active !== false);
                    // Build the selection model from stored rows (check_key null = whole sub-area).
                    const loaded = new Map();
                    for (const row of cs.subareas || []) {
                        if (row.check_key == null) { loaded.set(row.subarea_key, 'ALL'); continue; }
                        const cur = loaded.get(row.subarea_key);
                        if (cur === 'ALL') continue;
                        const s = cur instanceof Set ? cur : new Set();
                        s.add(row.check_key);
                        loaded.set(row.subarea_key, s);
                    }
                    setSel(loaded);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load configuration set');
        } finally {
            setLoading(false);
        }
    }, [id, isEdit]);

    useEffect(() => { load(); }, [load]);

    const groupKeyOf = useMemo(() => {
        const m = {};
        for (const g of groups) for (const sa of g.subAreas) m[sa.subarea_key] = g.group_key;
        return m;
    }, [groups]);

    // Selection state of one sub-area: 'all' | 'partial' | 'none'.
    const subState = (saKey) => {
        const v = sel.get(saKey);
        if (v === 'ALL') return 'all';
        if (v instanceof Set && v.size > 0) return 'partial';
        return 'none';
    };
    const checkOn = (saKey, checkKey) => {
        const v = sel.get(saKey);
        if (v === 'ALL') return true;
        return v instanceof Set && v.has(checkKey);
    };

    // Toggle whole sub-area: all -> none, otherwise -> all.
    const toggleSub = (saKey) => setSel(prev => {
        const next = new Map(prev);
        if (next.get(saKey) === 'ALL') next.delete(saKey);
        else next.set(saKey, 'ALL');
        return next;
    });

    // Toggle one check within a sub-area (collapses to 'ALL' when every check ends up selected).
    const toggleCheck = (sa, checkKey) => setSel(prev => {
        const next = new Map(prev);
        let cur = next.get(sa.subarea_key);
        if (cur === 'ALL') cur = new Set(sa.checks.map(c => c.key));
        else cur = new Set(cur instanceof Set ? cur : []);
        if (cur.has(checkKey)) cur.delete(checkKey); else cur.add(checkKey);
        if (cur.size === 0) next.delete(sa.subarea_key);
        else if (cur.size === sa.checks.length) next.set(sa.subarea_key, 'ALL');
        else next.set(sa.subarea_key, cur);
        return next;
    });

    const toggleExpand = (saKey) => setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(saKey)) next.delete(saKey); else next.add(saKey);
        return next;
    });

    const groupAll = (g) => g.subAreas.every(sa => sel.get(sa.subarea_key) === 'ALL');
    const toggleGroup = (g) => setSel(prev => {
        const next = new Map(prev);
        const all = g.subAreas.every(sa => next.get(sa.subarea_key) === 'ALL');
        g.subAreas.forEach(sa => { if (all) next.delete(sa.subarea_key); else next.set(sa.subarea_key, 'ALL'); });
        return next;
    });

    // Count of selected checks (for the header), expanding 'ALL' to the sub-area's check count.
    const checkCount = useMemo(() => {
        let n = 0;
        for (const g of groups) for (const sa of g.subAreas) {
            const v = sel.get(sa.subarea_key);
            if (v === 'ALL') n += sa.checks.length;
            else if (v instanceof Set) n += v.size;
        }
        return n;
    }, [groups, sel]);

    const handleSave = async () => {
        if (!name.trim()) { setNameErr('Name is required'); return; }
        // Build the flat selection rows (check_key null = whole sub-area).
        const subareas = [];
        for (const [saKey, v] of sel) {
            const gk = groupKeyOf[saKey] || null;
            if (v === 'ALL') subareas.push({ subarea_key: saKey, group_key: gk, check_key: null });
            else if (v instanceof Set) for (const ck of v) subareas.push({ subarea_key: saKey, group_key: gk, check_key: ck });
        }
        if (subareas.length === 0) { setError('Select at least one check'); return; }
        setSaving(true);
        setError(null);
        const payload = {
            name: name.trim(),
            description: description.trim() || null,
            active,
            subareas,
            account_id: selectedAccount || null,
            partner_id: selectedPartner || null,
        };
        try {
            if (isEdit) await dryftService.updateConfigSet(id, payload);
            else await dryftService.createConfigSet(payload);
            navigate(BACK);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save configuration set');
            setSaving(false);
        }
    };

    return (
        <div className="ppw-page">
            <div className="ppw-card">
                <div className="ppw-card-header">
                    <h2 className="ppw-card-title">{isEdit ? 'Edit configuration set' : 'New configuration set'}</h2>
                    <p className="ppw-card-subtitle">A named bundle of OHIP config checks a project can watch. Tick a whole area, or expand it to pick individual checks.</p>
                </div>

                {loading ? (
                    <div className="ppw-card-body"><p className="pi-empty-text">Loading…</p></div>
                ) : (
                    <div className="ppw-card-body">
                        {error && <div className="ppw-error-msg" style={{ marginBottom: '1rem' }}>{error}</div>}

                        <div className="occ-instance-block">
                            <label className="ppw-label">Name</label>
                            <input
                                type="text"
                                className="ppw-input"
                                placeholder="e.g. Rates"
                                value={name}
                                onChange={e => { setName(e.target.value); setNameErr(null); }}
                            />
                            {nameErr && <span className="ppw-error-msg">{nameErr}</span>}
                        </div>

                        <div className="occ-instance-block">
                            <label className="ppw-label">Description <span style={{ color: '#9ca3af' }}>(optional)</span></label>
                            <textarea
                                className="ppw-input"
                                rows={2}
                                placeholder="What this set covers."
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </div>

                        <div className="occ-instance-block">
                            <label className="ppw-label">Config checks <span style={{ color: '#9ca3af' }}>({checkCount} selected)</span></label>
                            <div className="dryft-cfg-groups">
                                {groups.map(g => (
                                    <div key={g.group_key} className="dryft-cfg-group">
                                        <label className="dryft-cfg-group-head">
                                            <input type="checkbox" checked={groupAll(g)} onChange={() => toggleGroup(g)} />
                                            <span className="dryft-cfg-group-title">{g.group_label}</span>
                                        </label>
                                        <div className="dryft-cfg-subareas">
                                            {g.subAreas.map(sa => {
                                                const st = subState(sa.subarea_key);
                                                const isOpen = expanded.has(sa.subarea_key);
                                                const total = sa.checks.length;
                                                const selCount = st === 'all' ? total : (sel.get(sa.subarea_key) instanceof Set ? sel.get(sa.subarea_key).size : 0);
                                                return (
                                                    <div key={sa.subarea_key} className="dryft-cfg-sa">
                                                        <div className="dryft-cfg-sa-head">
                                                            <label className="dryft-cfg-subarea">
                                                                <input
                                                                    type="checkbox"
                                                                    ref={el => { if (el) el.indeterminate = st === 'partial'; }}
                                                                    checked={st === 'all'}
                                                                    onChange={() => toggleSub(sa.subarea_key)}
                                                                />
                                                                <span>{sa.label}</span>
                                                            </label>
                                                            {total > 0 && (
                                                                <button type="button" className="dryft-cfg-expand" onClick={() => toggleExpand(sa.subarea_key)}>
                                                                    {st === 'partial' ? `${selCount}/${total}` : total} {isOpen ? '▾' : '▸'}
                                                                </button>
                                                            )}
                                                        </div>
                                                        {isOpen && total > 0 && (
                                                            <div className="dryft-cfg-checks">
                                                                {sa.checks.map(c => (
                                                                    <label key={c.key} className="dryft-cfg-check" title={c.ohipEndpoint || c.ohipEndpointTemplate || ''}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={checkOn(sa.subarea_key, c.key)}
                                                                            onChange={() => toggleCheck(sa, c.key)}
                                                                        />
                                                                        <span>{c.title}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="occ-instance-block">
                            <label className="dryft-cfg-subarea">
                                <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
                                <span>Active</span>
                            </label>
                        </div>
                    </div>
                )}

                <div className="ppw-footer">
                    <button className="ppw-btn-back" onClick={() => navigate(BACK)} disabled={saving}>← Cancel</button>
                    <button className="ppw-btn-next" onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create set')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DryftConfigSetForm;
